import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Center,
  Flex,
  VStack,
  HStack,
  Text,
  Textarea,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  IconButton,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Wrap,
  WrapItem,
  Input,
  InputGroup,
  InputLeftElement,
  Collapse,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  DeleteIcon,
  CopyIcon,
  AddIcon,
  EditIcon,
  HamburgerIcon,
  ChevronLeftIcon,
} from "@chakra-ui/icons";
import axios from "axios";
import { config } from "../config";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChatMap } from "./ChatMap";
import { formatList, primaryName } from "../utils";

const commodityColors = {
  gold: "yellow",
  silver: "gray",
  copper: "orange",
  zinc: "blue",
  lead: "purple",
  nickel: "green",
  lithium: "cyan",
  cobalt: "pink",
  iron: "red",
  uranium: "teal",
};

const getCommodityColor = (commodity) => {
  const lower = commodity?.toLowerCase();
  return commodityColors[lower] || "gray";
};

const MessageBubble = ({ message, isUser }) => {
  const toast = useToast();

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <Flex w="100%" justify={isUser ? "flex-end" : "flex-start"} px={4} py={2}>
      <Box
        maxW="80%"
        bg={isUser ? "amber.500" : "gray.100"}
        color={isUser ? "white" : "gray.800"}
        p={4}
        borderRadius="lg"
        boxShadow="sm"
        position="relative"
      >
        <HStack justify="space-between" mb={2} spacing={2}>
          <Text fontSize="xs" opacity={0.7}>
            {isUser ? "You" : "Assistant"}
          </Text>
          <IconButton
            size="xs"
            icon={<CopyIcon />}
            onClick={() => handleCopy(message)}
            aria-label="Copy message"
            variant="ghost"
            colorScheme={isUser ? "whiteAlpha" : "gray"}
            minW="auto"
            h="auto"
            p={1}
          />
        </HStack>
        <Box>
          {isUser ? (
            <Text whiteSpace="pre-wrap">{message}</Text>
          ) : (
            <Box
              sx={{
                "& p": {
                  marginBottom: "0.5em",
                },
                "& p:last-child": {
                  marginBottom: 0,
                },
                "& table": {
                  borderCollapse: "collapse",
                  width: "100%",
                  marginTop: "0.5em",
                  marginBottom: "0.5em",
                },
                "& th, & td": {
                  border: "1px solid",
                  borderColor: "gray.300",
                  padding: "0.5em",
                  textAlign: "left",
                },
                "& th": {
                  backgroundColor: "gray.200",
                  fontWeight: "bold",
                },
                "& tr:nth-of-type(even)": {
                  backgroundColor: "gray.50",
                },
                "& ul, & ol": {
                  paddingLeft: "1.5em",
                  marginBottom: "0.5em",
                },
                "& code": {
                  backgroundColor: "gray.200",
                  padding: "0.2em 0.4em",
                  borderRadius: "0.25em",
                  fontSize: "0.9em",
                },
                "& pre": {
                  backgroundColor: "gray.100",
                  padding: "1em",
                  borderRadius: "0.5em",
                  overflow: "auto",
                  marginBottom: "0.5em",
                },
                "& pre code": {
                  backgroundColor: "transparent",
                  padding: 0,
                },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message}
              </ReactMarkdown>
            </Box>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

const SearchResultCard = ({ result }) => {
  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={4}
      mb={3}
      bg="white"
      boxShadow="sm"
    >
      <VStack align="stretch" spacing={2}>
        <HStack justify="space-between">
          <Text fontWeight="bold" fontSize="lg">
            {primaryName(result.project_name, "Untitled Project")}
          </Text>
          {result.id && <Badge colorScheme="blue">ID: {result.id}</Badge>}
        </HStack>

        {result.company_name && (
          <Text fontSize="sm" color="gray.600">
            Company: {formatList(result.company_name, " | ")}
          </Text>
        )}

        {result.project_location && (
          <Text fontSize="sm" color="gray.600">
            Location: {result.project_location}
          </Text>
        )}

        {result.location_coordinates && (
          <Text fontSize="sm" color="gray.600">
            Coordinates: {result.location_coordinates[1]},{" "}
            {result.location_coordinates[0]}
          </Text>
        )}

        {result.commodity && result.commodity.length > 0 && (
          <Wrap>
            {result.commodity.map((c, idx) => (
              <WrapItem key={idx}>
                <Badge colorScheme={getCommodityColor(c)}>{c}</Badge>
              </WrapItem>
            ))}
          </Wrap>
        )}

        {result.deposit_style && (
          <Text fontSize="sm">
            <strong>Deposit Style:</strong> {result.deposit_style}
          </Text>
        )}

        {result.mineral_resources && (
          <Text fontSize="sm">
            <strong>Mineral Resources:</strong> {result.mineral_resources}
          </Text>
        )}

        {result.notes && (
          <Text fontSize="sm" color="gray.700">
            {result.notes}
          </Text>
        )}

        {result.matched_sections && result.matched_sections.length > 0 && (
          <Accordion allowToggle>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Text fontSize="sm" fontWeight="semibold">
                    Matched Sections ({result.matched_sections.length})
                  </Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <VStack align="stretch" spacing={3}>
                  {result.matched_sections.map((section, idx) => (
                    <Box key={idx} borderWidth="1px" borderRadius="md" p={3}>
                      <Text fontWeight="semibold" mb={2}>
                        {section.heading}
                      </Text>
                      {section.search_query && (
                        <Badge colorScheme="purple" mb={2}>
                          Query: {section.search_query}
                        </Badge>
                      )}
                      <Text
                        fontSize="sm"
                        color="gray.700"
                        whiteSpace="pre-wrap"
                      >
                        {section.body_text}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
      </VStack>
    </Box>
  );
};

const ChatSidebar = ({
  chats,
  currentChatId,
  onSelectChat,
  onCreateNewChat,
  onDeleteChat,
  loading,
  isOpen,
  onToggle,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredChats = chats.filter((chat) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      chat.session_id?.toLowerCase().includes(searchLower) ||
      chat.title?.toLowerCase().includes(searchLower) ||
      chat.summary?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      // Parse the UTC date string - ensure it's treated as UTC
      // ISO 8601 strings without timezone are often UTC from APIs
      let date;
      if (dateString.endsWith("Z") || dateString.match(/[+-]\d{2}:\d{2}$/)) {
        // Has explicit timezone info
        date = new Date(dateString);
      } else if (dateString.includes("T")) {
        // ISO format without timezone - assume UTC
        date = new Date(dateString + "Z");
      } else {
        // Fallback to direct parsing
        date = new Date(dateString);
      }

      // Verify the date is valid
      if (isNaN(date.getTime())) {
        console.warn("Invalid date string:", dateString);
        return dateString;
      }

      // Get current time in local timezone
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Show relative time for recent chats
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) {
        // For chats within a week, show relative time with local date
        return (
          date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          }) + ` (${diffDays}d ago)`
        );
      }

      // For older dates, show full date and time in local format
      // This will display in the user's browser locale and timezone
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (err) {
      console.error("Error formatting date:", err, dateString);
      return dateString;
    }
  };

  return (
    <Box
      position="relative"
      h="100vh"
      bg="gray.50"
      borderRight="1px solid"
      borderColor="gray.200"
      display="flex"
      flexDirection="column"
      w={isOpen ? "260px" : "48px"}
      overflow="hidden"
      transition="width 0.3s ease"
    >
      {/* Hamburger Menu Button - Always visible */}
      <Box
        position="absolute"
        top={2}
        left={isOpen ? "auto" : 2}
        right={isOpen ? 2 : "auto"}
        zIndex={10}
        bg="white"
        borderRadius="md"
        boxShadow="sm"
      >
        <IconButton
          icon={isOpen ? <ChevronLeftIcon /> : <HamburgerIcon />}
          onClick={onToggle}
          aria-label="Toggle sidebar"
          size="sm"
          variant="ghost"
          colorScheme="gray"
        />
      </Box>

      <Collapse in={isOpen} animateOpacity>
        <Box w="260px" h="100%" display="flex" flexDirection="column" pt={10}>
          {/* New Chat Button */}
          <Box p={3} borderBottom="1px solid" borderColor="gray.200">
            <Button
              w="100%"
              leftIcon={<AddIcon />}
              colorScheme="amber"
              onClick={onCreateNewChat}
              size="sm"
            >
              New Chat
            </Button>
          </Box>

          {/* Search */}
          <Box p={3} borderBottom="1px solid" borderColor="gray.200">
            <InputGroup size="sm">
              <Input
                placeholder="Search chats..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="white"
              />
            </InputGroup>
          </Box>

          {/* Chat List */}
          <Box flex="1" overflowY="auto">
            {loading ? (
              <Center p={4}>
                <Spinner size="sm" />
              </Center>
            ) : filteredChats.length === 0 ? (
              <Center p={4}>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  {searchTerm ? "No chats found" : "No chats yet"}
                </Text>
              </Center>
            ) : (
              <VStack align="stretch" spacing={0}>
                {filteredChats.map((chat) => (
                  <Box
                    key={chat.session_id}
                    px={3}
                    py={2}
                    cursor="pointer"
                    bg={
                      currentChatId === chat.session_id
                        ? "amber.100"
                        : "transparent"
                    }
                    _hover={{
                      bg:
                        currentChatId === chat.session_id
                          ? "amber.100"
                          : "gray.100",
                    }}
                    borderLeft={
                      currentChatId === chat.session_id
                        ? "3px solid"
                        : "3px solid transparent"
                    }
                    borderColor={
                      currentChatId === chat.session_id
                        ? "amber.500"
                        : "transparent"
                    }
                    onClick={() => onSelectChat(chat.session_id)}
                    position="relative"
                    group
                  >
                    <HStack justify="space-between" align="start">
                      <VStack align="start" spacing={0} flex="1" minW={0}>
                        <Text
                          fontSize="sm"
                          fontWeight={
                            currentChatId === chat.session_id
                              ? "semibold"
                              : "normal"
                          }
                          noOfLines={1}
                        >
                          {chat.title ||
                            chat.summary ||
                            chat.search_query ||
                            `Chat ${chat.session_id?.slice(-8)}`}
                        </Text>
                        {chat.started_at && (
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            title={(() => {
                              try {
                                const dateStr = chat.started_at;
                                let date;
                                if (
                                  dateStr.endsWith("Z") ||
                                  dateStr.match(/[+-]\d{2}:?\d{2}$/)
                                ) {
                                  date = new Date(dateStr);
                                } else if (dateStr.includes("T")) {
                                  date = new Date(dateStr + "Z");
                                } else {
                                  date = new Date(dateStr);
                                }
                                if (!isNaN(date.getTime())) {
                                  return date.toLocaleString(undefined, {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: true,
                                    timeZoneName: "short",
                                  });
                                }
                              } catch {}
                              return chat.started_at;
                            })()}
                          >
                            {formatDate(chat.started_at)}
                          </Text>
                        )}
                      </VStack>
                      <IconButton
                        size="xs"
                        icon={<DeleteIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteChat(chat.session_id);
                        }}
                        aria-label="Delete chat"
                        variant="ghost"
                        colorScheme="red"
                        opacity={0.6}
                        _hover={{ opacity: 1, bg: "red.50" }}
                      />
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export const Chat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const messagesEndRef = useRef(null);
  const [chatId, setChatId] = useState(null);
  const [chatData, setChatData] = useState(null);
  const [allChats, setAllChats] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingChats, setLoadingChats] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [geoBounds, setGeoBounds] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const chatIdFromUrl = searchParams.get("id");
  const reportIdFromUrl = searchParams.get("report_id");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatData?.messages]);

  // Load all chats
  useEffect(() => {
    loadAllChats();
  }, []);

  // Initialize or load chat
  useEffect(() => {
    const initializeChat = async () => {
      try {
        setInitializing(true);
        setError(null);

        if (chatIdFromUrl) {
          // Load existing chat
          await loadChat(chatIdFromUrl);
        }
        // Don't auto-create, just show empty state if no chatIdFromUrl
        setInitializing(false);
      } catch (err) {
        console.error("Error initializing chat:", err);
        setError("Failed to initialize chat");
        toast({
          title: "Error",
          description: "Failed to initialize chat",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setInitializing(false);
      }
    };

    initializeChat();
  }, [chatIdFromUrl, reportIdFromUrl]);

  const loadAllChats = async () => {
    try {
      setLoadingChats(true);
      const response = await axios.get(`${config.API_URL}/43101_chat/all`, {
        withCredentials: true,
      });
      setAllChats(response.data || []);
    } catch (err) {
      console.error("Error loading all chats:", err);
    } finally {
      setLoadingChats(false);
    }
  };

  const createChat = async (reportId = null) => {
    try {
      const newId = `chat_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const response = await axios.post(
        `${config.API_URL}/43101_chat/?id=${newId}`,
        {},
        { withCredentials: true }
      );
      setChatId(newId);
      setSearchParams({ id: newId });
      await loadChat(newId);
      await loadAllChats(); // Refresh chat list
    } catch (err) {
      console.error("Error creating chat:", err);
      throw err;
    }
  };

  const loadChat = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_URL}/43101_chat/`, {
        params: { id },
        withCredentials: true,
      });
      setChatData(response.data);
      setChatId(id);
      setError(null);
      // Convert geo_bounds from GeoJSON to string format if it exists
      if (response.data?.geo_bounds) {
        try {
          let geoJson;
          if (typeof response.data.geo_bounds === "string") {
            geoJson = JSON.parse(response.data.geo_bounds);
          } else {
            geoJson = response.data.geo_bounds;
          }

          // Extract coordinates and convert to string format
          if (
            geoJson.type === "Feature" &&
            geoJson.geometry?.type === "Polygon"
          ) {
            const coordinates = geoJson.geometry.coordinates[0];
            const polygonStr = coordinates
              .map((coord) => `${coord[0]},${coord[1]}`)
              .join("|");
            setGeoBounds(polygonStr);
          } else if (
            geoJson.type === "FeatureCollection" &&
            geoJson.features?.length > 0
          ) {
            const polygonFeature = geoJson.features.find(
              (f) => f.geometry?.type === "Polygon"
            );
            if (polygonFeature) {
              const coordinates = polygonFeature.geometry.coordinates[0];
              const polygonStr = coordinates
                .map((coord) => `${coord[0]},${coord[1]}`)
                .join("|");
              setGeoBounds(polygonStr);
            } else {
              setGeoBounds("");
            }
          } else {
            setGeoBounds("");
          }
        } catch (err) {
          console.error("Error parsing geo_bounds:", err);
          setGeoBounds("");
        }
      } else {
        setGeoBounds("");
      }
    } catch (err) {
      console.error("Error loading chat:", err);
      setError("Failed to load chat");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const selectChat = (id) => {
    setSearchParams({ id });
    setGeoBounds(""); // Clear geo bounds when switching chats
  };

  const sendMessage = async () => {
    if (!message.trim() || !chatId || sending) return;

    const userMessage = message.trim();
    setMessage("");
    setSending(true);
    setError(null);

    // Optimistically add user message to UI
    const optimisticUserMessage = {
      role: "user",
      content: userMessage,
    };
    setChatData((prev) => ({
      ...prev,
      messages: [...(prev?.messages || []), optimisticUserMessage],
    }));

    try {
      const encodedMessage = encodeURIComponent(userMessage);
      const geoBoundsParam = geoBounds ? encodeURIComponent(geoBounds) : "";
      const url = geoBoundsParam
        ? `${config.API_URL}/43101_chat/?id=${chatId}&message=${encodedMessage}&geo_bounds=${geoBoundsParam}`
        : `${config.API_URL}/43101_chat/?id=${chatId}&message=${encodedMessage}`;
      const response = await axios.put(url, {}, { withCredentials: true });

      // Reload chat to get updated messages and search results
      await loadChat(chatId);
      await loadAllChats(); // Refresh chat list to update titles
    } catch (err) {
      console.error("Error sending message:", err);
      setError("Failed to send message");
      // Remove optimistic message on error
      setChatData((prev) => ({
        ...prev,
        messages: (prev?.messages || []).slice(0, -1),
      }));
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  const deleteChat = async (idToDelete) => {
    if (!idToDelete) return;

    if (!window.confirm("Are you sure you want to delete this chat?")) {
      return;
    }

    try {
      await axios.delete(`${config.API_URL}/43101_chat/`, {
        params: { id: idToDelete },
        withCredentials: true,
      });
      toast({
        title: "Chat deleted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // If deleting current chat, navigate to empty state
      if (idToDelete === chatId) {
        setChatId(null);
        setChatData(null);
        setSearchParams({});
      }

      await loadAllChats(); // Refresh chat list
    } catch (err) {
      console.error("Error deleting chat:", err);
      toast({
        title: "Error",
        description: "Failed to delete chat",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (initializing) {
    return (
      <Flex h="100vh">
        <ChatSidebar
          chats={allChats}
          currentChatId={chatId}
          onSelectChat={selectChat}
          onCreateNewChat={createChat}
          onDeleteChat={deleteChat}
          loading={loadingChats}
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />
        <Center flex="1">
          <VStack spacing={4}>
            <Spinner size="xl" color="amber.500" />
            <Text>Loading chat...</Text>
          </VStack>
        </Center>
      </Flex>
    );
  }

  const messages = chatData?.messages || [];
  // Parse search_results from JSON string if needed
  let searchResults = [];
  if (chatData?.search_results) {
    try {
      if (typeof chatData.search_results === "string") {
        searchResults = JSON.parse(chatData.search_results);
      } else {
        searchResults = chatData.search_results;
      }
    } catch (e) {
      console.error("Error parsing search_results:", e);
      searchResults = [];
    }
  }

  return (
    <Flex h="100vh" bg="white">
      {/* Sidebar */}
      <ChatSidebar
        chats={allChats}
        currentChatId={chatId}
        onSelectChat={selectChat}
        onCreateNewChat={createChat}
        onDeleteChat={deleteChat}
        loading={loadingChats}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Main Content Area - Split into Chat and Map */}
      <Flex flex="1" bg="white">
        {/* Left Half - Chat */}
        <Flex
          flex="1"
          flexDirection="column"
          bg="white"
          borderRight="1px solid"
          borderColor="gray.200"
        >
          {/* Messages Area */}
          <Box flex="1" overflowY="auto" bg="white" px={0}>
            {!chatId ? (
              <Center h="100%">
                <VStack spacing={4}>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.700">
                    Start a new conversation
                  </Text>
                  <Text fontSize="md" color="gray.500" textAlign="center">
                    Ask questions about reports, projects, or mining data
                  </Text>
                  <Button
                    colorScheme="amber"
                    leftIcon={<AddIcon />}
                    onClick={createChat}
                    size="lg"
                  >
                    New Chat
                  </Button>
                </VStack>
              </Center>
            ) : loading && messages.length === 0 ? (
              <Center h="100%">
                <VStack spacing={4}>
                  <Spinner size="xl" color="amber.500" />
                  <Text>Loading messages...</Text>
                </VStack>
              </Center>
            ) : messages.length === 0 ? (
              <Center h="100%">
                <VStack spacing={4}>
                  <Text fontSize="lg" color="gray.500">
                    Start a conversation
                  </Text>
                  <Text fontSize="sm" color="gray.400" textAlign="center">
                    Ask questions about reports, projects, or mining data
                  </Text>
                </VStack>
              </Center>
            ) : (
              <VStack align="stretch" spacing={0} py={4}>
                {messages
                  .filter((msg) => msg.role !== "system") // Filter out system messages
                  .map((msg, idx) => {
                    // Handle different message formats
                    let messageText = "";
                    let isUser = false;

                    if (typeof msg === "string") {
                      messageText = msg;
                      isUser = false;
                    } else if (msg.role === "user") {
                      // For user messages, show search_query if available, otherwise content
                      messageText =
                        msg.search_query || msg.content || msg.message || "";
                      isUser = true;
                    } else if (msg.content) {
                      // For assistant messages, show content
                      messageText = msg.content;
                      isUser = false;
                    } else if (msg.message) {
                      messageText = msg.message;
                      isUser = msg.role === "user" || msg.isUser || false;
                    } else if (msg.response) {
                      messageText = msg.response;
                      isUser = false;
                    }

                    return (
                      <MessageBubble
                        key={idx}
                        message={messageText}
                        isUser={isUser}
                      />
                    );
                  })}
                {sending && (
                  <Flex w="100%" justify="flex-start" px={4} py={2}>
                    <HStack>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="gray.500">
                        Sending...
                      </Text>
                    </HStack>
                  </Flex>
                )}
                <div ref={messagesEndRef} />
              </VStack>
            )}

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box px={4} py={4} borderTop="1px solid" borderColor="gray.200">
                <Accordion allowToggle defaultIndex={[]}>
                  <AccordionItem border="none">
                    <AccordionButton px={0} py={2}>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="bold">
                          Context ({searchResults.length})
                        </Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel px={0} pb={0}>
                      <VStack align="stretch" spacing={2} mt={3}>
                        {searchResults.map((result, idx) => (
                          <SearchResultCard key={idx} result={result} />
                        ))}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </Box>
            )}
          </Box>

          {/* Input Area */}
          {chatId && (
            <Box borderTop="1px solid" borderColor="gray.200" p={4} bg="white">
              {error && (
                <Alert status="error" mb={4} size="sm">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
              <HStack spacing={2}>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
                  resize="none"
                  rows={3}
                  isDisabled={sending}
                  bg="white"
                  borderColor="gray.300"
                  _focus={{ borderColor: "amber.400" }}
                />
                <Button
                  onClick={sendMessage}
                  isDisabled={!message.trim() || sending}
                  isLoading={sending}
                  colorScheme="amber"
                  size="lg"
                  px={10}
                  fontSize="md"
                  fontWeight="semibold"
                >
                  Send
                </Button>
              </HStack>
            </Box>
          )}
        </Flex>

        {/* Right Half - Map */}
        <Box w="50%" h="100%" p={4} bg="slate.50">
          <ChatMap
            searchResults={searchResults}
            geoBounds={
              chatData?.geo_bounds
                ? (() => {
                    try {
                      return typeof chatData.geo_bounds === "string"
                        ? JSON.parse(chatData.geo_bounds)
                        : chatData.geo_bounds;
                    } catch {
                      return null;
                    }
                  })()
                : null
            }
            onPolygonChange={(polygonStr) => {
              setGeoBounds(polygonStr);
            }}
          />
        </Box>
      </Flex>
    </Flex>
  );
};
